/**
 * What the assistant is told about /audience (spec 2026-10-21 d3-d4 chat §3): a LookerChatContext with template.id
 * "audience", read by the bubble in mode 'audience' with AUDIENCE_ADVISOR, whose page rules explain its keys. Built
 * from the CSV's own input, so the chat and the file read one view. Two tiles, the page's two boxes: Segment over
 * every row, and the ad-set table's first LOOKER_CHAT_ROW_CAP rows with "รวม" as `totals`, so the cap never cuts it.
 * Cells are worded as the page draws them, and a figure the page prints as "—" is null. A failed read is told as
 * failed; under either card both tiles are empty with the card's sentence. The page tells nothing while the table
 * loads. No backfill: this page reads none. Pure.
 *
 * @module services/audience/audienceChatContext
 */
import { CHANNEL_LABEL, campaignStateText, emptySentence, familyLabel, type TFn } from '@/services/channels/channelFormat';
import type { SegmentPoint } from '@/services/channels/channelTypes';
import { channelFilterText, objectiveFilterText } from '@/services/creatives/creativeText';
import {
  freshnessOfState, LOOKER_CHAT_ROW_CAP, type LookerChatContext, type LookerChatTile,
} from '@/services/looker/lookerChatContext';
import type { DateRange } from '@/services/looker/lookerDateRange';
import { unit } from '@/services/overview/boxTableParts';
import { DASH, fmtCount, fmtMoney, fmtPct } from '@/services/overview/overviewFormat';
import type { AudienceCsvInput } from './audienceCsv';
import { activeFilterText, dataNotesOf, emptyTextOf, footnotesOf } from './audienceText';
import type { AudienceTableData, AudienceTotal } from './audienceTypes';

export const AUDIENCE_TEMPLATE_ID = 'audience';

/** The CSV's input and the window before the period, so the chat and the file read one view (spec §3.2). */
export interface AudienceChatInput extends AudienceCsvInput {
  previous: DateRange;
}

type Row = Record<string, string | null>;

/** A figure as the page draws it; the page's "—" is null (spec §3.2's table). */
const shown = (text: string): string | null => (text === DASH ? null : text);

/** Box 1 as the CSV's section 1 heads it: Segment · Share of spend (%) · CTR (%) · CPC (currency) · n<500. */
const segmentColumns = (t: TFn, currency: string | null): string[] => [
  t('audience.col.segment'), unit(t('channels.breakdown.segmentShare'), '%'), unit(t('channels.breakdown.segmentCtr'), '%'),
  unit(t('channels.breakdown.segmentCpc'), currency), t('overview.lowSample'),
];

/** Box 2's columns with the chip and the marker it draws, and the CSV's units in the headers. */
const tableColumns = (t: TFn, currency: string | null): string[] => [
  t('audience.col.adSet'), t('audience.csv.status'), t('audience.col.channel'), t('audience.col.segment'),
  unit(t('audience.col.spend'), currency), unit(t('audience.col.share'), '%'), t('audience.col.impressions'),
  unit(t('audience.col.ctr'), '%'), unit(t('audience.col.cpc'), currency), unit(t('audience.col.cpm'), currency),
  t('overview.lowSample'),
];

/** One family as SegmentCharts draws it: Share to one decimal, CTR, CPC; null where a chart draws no bar. */
const segmentRow = (s: SegmentPoint, columns: readonly string[], currency: string | null, t: TFn): Row => {
  const cells = [
    familyLabel(s.family, t), shown(fmtPct(s.share, 1)), shown(fmtPct(s.ctr)), shown(fmtMoney(s.cpc, currency)),
    s.lowSample ? t('overview.lowSample') : null,
  ];
  return Object.fromEntries(columns.map((c, i) => [c, cells[i]]));
};

/** The six figures every line draws, "รวม" included, and its n<500 marker. */
const figureCells = (m: AudienceTotal, currency: string | null, t: TFn): Array<string | null> => [
  shown(fmtMoney(m.spend, currency)), shown(fmtPct(m.share, 1)), shown(fmtCount(m.impressions)), shown(fmtPct(m.ctr)),
  shown(fmtMoney(m.cpc, currency)), shown(fmtMoney(m.cpm, currency)), m.lowSample ? t('overview.lowSample') : null,
];

/** The rows the table draws, and "รวม" with its text cells null, as the file writes it. */
const tableRows = (
  data: AudienceTableData, columns: readonly string[], currency: string | null, t: TFn,
): Required<Pick<LookerChatTile, 'rows' | 'totals'>> => {
  const line = (cells: Array<string | null>): Row => Object.fromEntries(columns.map((c, i) => [c, cells[i]]));
  return {
    rows: data.rows.slice(0, LOOKER_CHAT_ROW_CAP).map((r) => line([
      r.name, campaignStateText(r.state, t), CHANNEL_LABEL[r.platform], familyLabel(r.family, t), ...figureCells(r, currency, t),
    ])),
    totals: line([t('channels.breakdown.total'), null, null, null, ...figureCells(data.total, currency, t)]),
  };
};

const tilesOf = (input: AudienceChatInput, currency: string | null, t: TFn): LookerChatTile[] => {
  const { view } = input;
  const base = { rowsOmitted: 0, moreRowsThanLoaded: false };
  const segments = { ...base, title: t('audience.box.segments'), type: 'bar' as const };
  const table = { ...base, title: t('audience.box.table'), type: 'table' as const };
  if (view.table.status === 'failed') {
    return [{ ...segments, status: 'failed', columns: [], rows: [] }, { ...table, status: 'failed', columns: [], rows: [] }];
  }
  // Loading: not told yet (the page mounts no chat until the table is read).
  if (view.table.status !== 'ready') return [];
  const { data } = view.table;
  const footnotes = { leftOut: view.leftOut, unknownCurrency: view.unknownCurrency };
  if (data.rows.length === 0) {
    // Under a card the page draws its sentence and keeps the notes about the data (rules-04 A3); else each box's line.
    const scope = input.allWorkspaces ? t('looker.allWorkspaces') : input.workspaceName ?? '';
    const line = view.empty ? emptySentence(view.empty, t) : emptyTextOf(data, scope, input.filters, t);
    const notes = view.empty ? dataNotesOf(footnotes, t) : footnotesOf(footnotes, t);
    return [
      { ...segments, status: 'empty', columns: [], rows: [], coverageNotes: [line] },
      { ...table, status: 'empty', columns: [], rows: [], coverageNotes: [line, ...notes] },
    ];
  }
  const segmentHead = segmentColumns(t, currency);
  const tableHead = tableColumns(t, currency);
  const lowSample = data.segments.some((s) => s.lowSample);
  return [
    {
      ...segments, status: 'ok', columns: segmentHead,
      rows: data.segments.map((s) => segmentRow(s, segmentHead, currency, t)),
      ...(lowSample ? { coverageNotes: [t('channels.breakdown.segmentLowSample')] } : {}),
    },
    {
      ...table, status: 'ok', columns: tableHead, ...tableRows(data, tableHead, currency, t),
      rowsOmitted: Math.max(data.rows.length - LOOKER_CHAT_ROW_CAP, 0),
      coverageNotes: footnotesOf(footnotes, t),
    },
  ];
};

export const buildAudienceChatContext = (input: AudienceChatInput, t: TFn): LookerChatContext => {
  const { view, filters: f } = input;
  const currency = view.money?.currency ?? null;
  return {
    ...freshnessOfState(input.freshness),
    kind: 'looker',
    template: { id: AUDIENCE_TEMPLATE_ID, title: t('audience.title') },
    scope: input.allWorkspaces ? { allWorkspaces: true, workspaceName: null } : { allWorkspaces: false, workspaceName: input.workspaceName },
    // As the file's filters line words them; the period travels as `period`.
    filters: [
      { title: t('overview.filters.objective'), value: objectiveFilterText(f.objective, t) },
      { title: t('overview.filters.channels'), value: channelFilterText(f.channels, t) },
      { title: t('audience.csv.campaign'), value: activeFilterText(f.activeOnly, view.hidden, t) },
    ],
    period: { start: input.range.start, end: input.range.end, previousStart: input.previous.start, previousEnd: input.previous.end },
    latestDataDate: input.dataDate,
    currency,
    // As D2's: with no currency too, when the page left an unknown currency out (a card never does, audienceView).
    ...(view.money?.unknownExcluded ? { currencyUnknownExcluded: true as const } : {}),
    tiles: tilesOf(input, currency, t),
  };
};
